import { ITimeObject, Timepicker, TimepickerEvents } from "@dhx/ts-timepicker";
import { create, el, awaitRedraw } from "@dhx/ts-common/dom";
import { isEmptyObj } from "@dhx/ts-common/core";
import { Label } from "./helper/label";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import {
	ValidationStatus,
	ITimePickerConfig,
	ITimePickerEventHandlersMap,
	ItemEvent,
	ITimePicker,
	ITimePickerProps,
} from "../types";
import {
	baseProps,
	getFormItemCss,
	getValidationMessage,
	isTimeFormat,
	isVerify,
	widgetConfig,
} from "../helper";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";

export class TimePicker extends Label implements ITimePicker {
	public config: ITimePickerConfig;
	public timepicker: Timepicker;
	public events: IEventSystem<ItemEvent, ITimePickerEventHandlersMap> = new EventSystem();

	private _keyManager: IKeyManager;
	private _popup: Popup;
	private _isValid = true;
	private _popupIsFocus = false;
	private _propsItem = [
		"required",
		"validation",
		"icon",
		"placeholder",
		"editable",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
	];
	private _propsTimepicker = ["timeFormat", "controls", "valueFormat"];
	private _props = [...baseProps, ...this._propsItem, ...this._propsTimepicker];

	constructor(container, config: ITimePickerConfig) {
		super(null, config);

		this._keyManager = new KeyManager(() => {
			const activeEl = document.activeElement;
			const element = this.getRootView()?.refs?.input?.el;

			return element === activeEl || this._popup.isVisible();
		});

		this._initView(config);
		this._initHandlers();
		this._initHotkeys();

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor() {
		this.events && this.events.clear();
		this.timepicker && this.timepicker.destructor();
		this._popup && this._popup.destructor();

		this.events = this._uid = this._propsItem = this._propsTimepicker = this._props = this._isValid = null;
		super._destructor();
		this.unmount();
	}
	setProperties(propertyConfig: ITimePickerProps): void {
		awaitRedraw().then(() => {
			if (
				!propertyConfig ||
				isEmptyObj(propertyConfig) ||
				!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
			)
				return;

			for (const key in propertyConfig) {
				if (this._props.includes(key)) {
					this.config[key] = propertyConfig[key];
				}
			}
			this._initView(this.config);
			this._initHandlers();

			this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
			this.paint();
		});
	}
	getProperties(): ITimePickerProps {
		const obj: ITimePickerProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		if (
			!this.config.hidden ||
			!this.events.fire(ItemEvent.beforeShow, [this.getValue(this.config.valueFormat === "timeObject")])
		) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [this.getValue(this.config.valueFormat === "timeObject")]);
	}
	hide(init?: boolean): void {
		if (
			(this.config.hidden && !init) ||
			!this.events.fire(ItemEvent.beforeHide, [
				this.getValue(this.config.valueFormat === "timeObject"),
				init,
			])
		) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [
			this.getValue(this.config.valueFormat === "timeObject"),
			init,
		]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	validate(silent = false, validateValue?: string | ITimeObject): boolean {
		const value =
			typeof validateValue === "undefined"
				? this.getValue(this.config.valueFormat === "timeObject")
				: validateValue;

		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [value])) return;
		this._isValid = this.config.validation
			? this.config.validation(value)
			: isTimeFormat(this.getValue(), this.config.timeFormat);
		!silent && this.events.fire(ItemEvent.afterValidate, [value, this._isValid]);

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(value: Date | number | string | any[] | ITimeObject): void {
		if (typeof value === "undefined" || value === this.config.value) return;
		this.timepicker.setValue(value);
		if (this.config.controls) {
			this.config.value = this.timepicker.getValue();
			this.events.fire(ItemEvent.change, [this.getValue(this.config.valueFormat === "timeObject")]);
			isVerify(this.config) && this.validate();
			this.paint();
		}
	}
	getValue(asOBject?: boolean): ITimeObject | string | any {
		return asOBject ? this.timepicker.getValue(asOBject) : this.config.value || "";
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this._popup.hide();
			this.getRootView().refs.input.el.blur();
		});
	}
	clear(): void {
		if (this.config.value === "") return;
		this._clear();
	}
	getWidget(): Timepicker {
		return this.timepicker;
	}
	protected _initView(config: ITimePickerConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.timepicker) {
			this.timepicker.destructor();
		}
		if (this._popup) {
			this._popup.destructor();
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			editable: false,
			hidden: false,
			timeFormat: 24,
			controls: false,
			valueFormat: "string",
			required: false,
			icon: "",
			placeholder: "",
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		this._popup = new Popup();
		this.timepicker = new Timepicker(null, widgetConfig(config));
		this._popup.attach(this.timepicker);

		if (this.config.hasOwnProperty("value")) {
			this.config.value = this.timepicker.getValue();
		}

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
	}
	protected _initHandlers() {
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
		if (this.config.controls) {
			this.timepicker.events.on(TimepickerEvents.afterClose, () => {
				this._popup.hide();
			});
			this.timepicker.events.on(TimepickerEvents.afterApply, () => {
				this._afterApply();
			});
			this._popup.events.on(PopupEvents.afterHide, () => {
				if (this.config.value && this.config.value !== this.timepicker.getValue()) {
					this.timepicker.setValue(this.config.value);
				} else if (this.config.value === "" || this.config.value === undefined) {
					this._clear(true);
				}
			});
		} else {
			this.timepicker.events.on(TimepickerEvents.change, () => {
				this.config.value = this.timepicker.getValue();
				this.events.fire(ItemEvent.change, [this.getValue(this.config.valueFormat === "timeObject")]);
				this.paint();
			});
		}
		this._popup.events.on(PopupEvents.afterHide, () => {
			if (document.activeElement !== this.getRootView().refs.input.el) {
				this.events.fire(ItemEvent.blur, [this.getValue()]);
			}
			this.paint();
		});
		this.events.on(ItemEvent.input, (value: string) => {
			const timeLength = this.config.timeFormat === 12 ? 7 : 5;
			if (value.length >= timeLength && isTimeFormat(value, this.config.timeFormat)) {
				this.timepicker.setValue(value);
			} else if (value === "" && !this.config.controls) {
				this.clear();
			}
		});
		this.events.on(ItemEvent.afterChangeProperties, () => {
			this.config.value = this.timepicker.getValue();
			this.paint();
		});
		this.events.on(ItemEvent.blur, () => {
			this._popupIsFocus = false;
			this.paint();
		});
		document.addEventListener("keydown", (event: KeyboardEvent) => {
			const activeEl = document.activeElement;
			const element = this.getRootView()?.refs?.input?.el;

			if (element === activeEl || this._popup.isVisible()) {
				this.events.fire(ItemEvent.keydown, [event]);
			}
		});
		document.addEventListener("mousedown", () => {
			const popup = this._popup.getContainer();
			const activeEl = document.activeElement;
			if (popup !== activeEl) this._popupIsFocus = false;
		});
	}
	protected _getHandlers() {
		return {
			onfocus: () => {
				if (this._popup.isVisible()) {
					return;
				}
				const node = this.getRootView().refs.input.el;
				this._popup.show(node);
				this.events.fire(ItemEvent.focus, [this.getValue()]);
				this.paint();
			},
			onblur: () => {
				if (!this._popup.isVisible()) {
					this.events.fire(ItemEvent.blur, [this.getValue()]);
				}
				this.paint();
			},
			oninput: (e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				this.events.fire(ItemEvent.input, [value]);
			},
		};
	}
	protected _initHotkeys() {
		const handlers = {
			"shift+tab": () => {
				this._applyTab();
			},
			tab: () => {
				this._applyTab();
			},
			escape: () => {
				if (this._popup.isVisible()) {
					this.getRootView().refs.input.el.focus();
				}
				this._popup.hide();
			},
			enter: (event: Event) => {
				event.preventDefault();
				const node = this.getRootView().refs.input.el;
				if (!this._popup.isVisible()) {
					this._popup.show(node);
				} else if (!this._popupIsFocus || !this.config.controls) {
					if (this.config.value && node.value === "") {
						this.clear();
					} else if (node.value !== this.config.value) {
						this.config.value = this.timepicker.getValue();
						this.events.fire(ItemEvent.change, [
							this.getValue(this.config.valueFormat === "timeObject"),
						]);
					}
					isVerify(this.config) && this.validate();
					this._popup.hide();
				}
			},
			arrowRight: (event: Event) => {
				const focusElement = this._popup.getContainer();
				if (this._popup.isVisible() && !this._popupIsFocus) {
					event.preventDefault();
					focusElement.focus();
					this._popupIsFocus = true;
				}
			},
		};

		for (const key in handlers) {
			this._keyManager.addHotKey(key, handlers[key]);
		}
	}
	protected _draw() {
		const {
			value,
			required,
			disabled,
			placeholder,
			name,
			id,
			editable,
			label,
			helpMessage,
		} = this.config;

		const activeFocus =
			this._popup.isVisible() || this.getRootView()?.refs?.input?.el === document.activeElement;

		return el(
			"div.dhx_form-group",
			{
				class: getFormItemCss(this.config, isVerify(this.config), activeFocus),
			},
			[
				this._drawLabel(),
				el(".dhx_input__wrapper", [
					el("div.dhx_input__container", {}, [
						el(".dhx_input__icon.dxi.dxi-clock-outline"),
						el("input.dhx_input.dhx_input--icon-padding", {
							tabindex: 0,
							"data-dhx-id": name || id,
							_key: this._uid,
							_ref: "input",
							value,
							type: "text",
							required,
							disabled,
							class: activeFocus && "dhx_input--focus",
							placeholder: placeholder || "",
							name: name || "",
							id: id || this._uid,
							onfocus: this._handlers.onfocus,
							onblur: this._handlers.onblur,
							oninput: this._handlers.oninput,
							autocomplete: "off",
							readOnly: !editable,
							"aria-label": label || `${editable ? "type or" : ""} select date`,
							"aria-describedby": helpMessage ? `dhx_label__help_${id || this._uid}` : null,
						}),
					]),
					getValidationMessage(this.config) &&
						el("span.dhx_input__caption", {}, getValidationMessage(this.config)),
				]),
			]
		);
	}
	private _clear(silent?: boolean) {
		this.timepicker.clear();
		this.config.value = "";
		!silent && this.events.fire(ItemEvent.change, [this.config.value]);
	}
	private _afterApply() {
		const inputValue = this.getRootView().refs.input.el.value;
		if (this.config.value && inputValue === "") {
			this.clear();
		} else {
			this.config.value = this.timepicker.getValue();
			this.events.fire(ItemEvent.change, [this.getValue(this.config.valueFormat === "timeObject")]);
		}
		isVerify(this.config) && this.validate();
		this._popup.hide();
		this.paint();
	}
	private _applyTab() {
		const activeEl = document.activeElement;
		const popup = this._popup.getRootView()?.refs?.content?.el;
		if (activeEl !== popup && !this._popupIsFocus) {
			this._popup.hide();
		}
	}
}
